import { FC, useEffect } from 'react';

import { useFormikContext } from 'formik';
import { Autocomplete, Input, TagsField } from 'ui/components/form';
import { Button } from 'ui/components/common';
import FILTER_TYPES from 'ui/components/form/FilterForm/filterTypes';
import useModal from 'ui/partials/Modal/useModal';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { useTour } from '@reactour/tour';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { compact } from 'lodash';

import { SIMPLE_COMPANY_FORM } from './schema';

type SimpleCompanyFormProps = {
  onCancel?: () => void;
};
const SimpleCompanyForm: FC<SimpleCompanyFormProps> = ({ onCancel }) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setFieldError,
    setErrors,
    setValues,
  } = useFormikContext<SIMPLE_COMPANY_FORM>();
  const { isOpen } = useTour();

  useEffect(() => {
    if (isOpen) {
      setValues({ name: 'Raging Bull' });
    } else {
      setValues({ name: '' });
    }
  }, [isOpen, setValues]);

  const { hideModal } = useModal();
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      hideModal();
    }
  };

  const filterIndustry = (inputValue: string) => {
    return FILTER_TYPES.industry.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };
  const filterCompanyTypes = (inputValue: string) => {
    return FILTER_TYPES.companyTypes.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const loadIndustryOptions = (
    inputValue: string,
    callback: (options: typeof FILTER_TYPES.industry) => void,
  ) => {
    setTimeout(() => {
      callback(filterIndustry(inputValue));
    }, 1000);
  };
  const loadCompanyTypesOptions = (
    inputValue: string,
    callback: (options: typeof FILTER_TYPES.companyTypes) => void,
  ) => {
    setTimeout(() => {
      callback(filterCompanyTypes(inputValue));
    }, 1000);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        data-tut={TOUR_SELECTOR.TOUR.CONTACT.COMPANY.FORM.GENERAL.FORM}
        className="flex flex-col gap-y-4"
      >
        <Input
          className="col-span-2 w-full"
          type="text"
          name="name"
          size={SIZE.Small}
          textType={errors.name && touched.name ? 'danger' : 'default'}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          placeholder="Company name"
          subText={errors.name && touched.name ? errors.name : undefined}
          label="Company name"
          required
          disabled={isSubmitting}
        />
        <Autocomplete
          className="w-full"
          name="industry"
          size={SIZE.Medium}
          state={errors.industry && touched.industry ? 'danger' : undefined}
          onChange={(val: any) => setFieldValue('industry', val.value)}
          defaultValue={[values?.industry]}
          placeholder="Select"
          subText={errors.industry && touched.industry ? errors.industry : ''}
          label="Industry"
          loadOptions={loadIndustryOptions}
          isDisabled={isSubmitting}
        />
        <Autocomplete
          className="right w-full"
          name="type"
          size={SIZE.Medium}
          state={errors.type && touched.type ? 'danger' : undefined}
          onChange={(val: any) => setFieldValue('type', val.value)}
          defaultValue={[values?.type]}
          placeholder="Select"
          subText={errors.type && touched.type ? errors.type : ''}
          label="Type"
          loadOptions={loadCompanyTypesOptions}
          isDisabled={isSubmitting}
        />
        <TagsField
          className="col-span-4 w-full"
          onChange={(val) =>
            setFieldValue(
              'tagId',
              val?.map((tag) => tag.value),
            )
          }
          disabled={isSubmitting}
          defaultValue={compact(values.tagId)}
          setError={(err) => {
            if (err) {
              setFieldError(
                'tagId',
                'Tags cannot have spaces and special characters.',
              );
            } else {
              setErrors({});
            }
          }}
        />
      </div>
      <div
        className={cn(
          'mt-5 pt-5',
          'flex gap-3 justify-end',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={handleCancel}
          type="button"
          disabled={isSubmitting}
          label="CANCEL"
          intent="tertiary"
          size="sm"
        />
        <Button
          data-tut={TOUR_SELECTOR.TOUR.CONTACT.COMPANY.FORM.GENERAL.SAVE_BUTTON}
          disabled={isSubmitting}
          label="SAVE"
          type="submit"
          size="sm"
        />
      </div>
    </form>
  );
};

export default SimpleCompanyForm;
